import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { Menu, Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

const navigationItemSchema = z.object({
  label: z.string().min(1, "Label is required").max(50, "Label must be 50 characters or less"),
  path: z.string().min(1, "Path is required").max(200, "Path must be 200 characters or less"),
  icon: z.string().max(50, "Icon name must be 50 characters or less").optional(),
  display_order: z.number().int().min(0).default(0),
  requires_admin: z.boolean().default(false),
  is_external: z.boolean().default(false),
  active: z.boolean().default(true),
});

type NavigationItemFormData = z.infer<typeof navigationItemSchema>;

interface NavigationItem extends NavigationItemFormData {
  id: string;
  created_at: string;
  updated_at: string;
  is_external: boolean;
}

export const AdminNavigation = () => {
  const [items, setItems] = useState<NavigationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<NavigationItem | null>(null);
  const { toast } = useToast();

  const form = useForm<NavigationItemFormData>({
    resolver: zodResolver(navigationItemSchema),
    defaultValues: {
      label: "",
      path: "",
      icon: "",
      display_order: 0,
      requires_admin: false,
      is_external: false,
      active: true,
    },
  });

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from("navigation_items")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error("Error fetching navigation items:", error);
      toast({
        title: "Error",
        description: "Failed to load navigation items",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleSave = async (values: NavigationItemFormData) => {
    try {
      if (editingItem) {
        const { error } = await supabase
          .from("navigation_items")
          .update(values)
          .eq("id", editingItem.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Navigation item updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("navigation_items")
          .insert([{
            label: values.label,
            path: values.path,
            icon: values.icon || null,
            display_order: values.display_order,
            requires_admin: values.requires_admin,
            is_external: values.is_external,
            active: values.active,
          }]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Navigation item created successfully",
        });
      }

      setDialogOpen(false);
      setEditingItem(null);
      form.reset();
      fetchItems();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save navigation item",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: NavigationItem) => {
    setEditingItem(item);
    form.reset({
      label: item.label,
      path: item.path,
      icon: item.icon || "",
      display_order: item.display_order,
      requires_admin: item.requires_admin,
      is_external: item.is_external,
      active: item.active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this navigation item?")) return;

    try {
      const { error } = await supabase
        .from("navigation_items")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Navigation item deleted successfully",
      });

      fetchItems();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete navigation item",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (item: NavigationItem) => {
    try {
      const { error } = await supabase
        .from("navigation_items")
        .update({ active: !item.active })
        .eq("id", item.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Navigation item ${!item.active ? 'activated' : 'deactivated'}`,
      });

      fetchItems();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update navigation item",
        variant: "destructive",
      });
    }
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingItem(null);
      form.reset();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading navigation items...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Menu className="h-5 w-5" />
          <h2 className="text-2xl font-bold">Navigation Menu ({items.length})</h2>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingItem ? "Edit" : "Create"} Navigation Item</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Label</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Dashboard" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="path"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Path</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="/dashboard or https://example.com" />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Internal path (e.g., /dashboard) or external URL (e.g., https://example.com)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="icon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Icon Name (Lucide)</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="LayoutDashboard" />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Use Lucide React icon names (e.g., LayoutDashboard, Trophy, Fish)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="display_order"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Order</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          {...field} 
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="requires_admin"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Admin Only</FormLabel>
                        <FormDescription className="text-xs">
                          Only show to admin users
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="is_external"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>External Link</FormLabel>
                        <FormDescription className="text-xs">
                          Opens in a new tab (for external URLs)
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Active</FormLabel>
                        <FormDescription className="text-xs">
                          Show in navigation menu
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full">
                  {editingItem ? "Update" : "Create"} Navigation Item
                </Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No navigation items found
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <GripVertical className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold truncate">{item.label}</p>
                        {item.requires_admin && (
                          <Badge variant="secondary" className="text-xs">Admin</Badge>
                        )}
                        {item.is_external && (
                          <Badge variant="outline" className="text-xs">External</Badge>
                        )}
                        {!item.active && (
                          <Badge variant="outline" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="truncate">{item.path}</span>
                        {item.icon && <span>Icon: {item.icon}</span>}
                        <span>Order: {item.display_order}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleToggleActive(item)}
                      title={item.active ? "Deactivate" : "Activate"}
                    >
                      <Switch checked={item.active} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(item)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(item.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
